// Number line data types
interface Margins {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface AxisConfig {
  minimum: number;
  maximum: number;
  labelSpacing?: number;
  tickSpacing?: number;
  showMinArrow?: boolean;
  showMaxArrow?: boolean;
  labelFontFamily?: string;
  labelFontSize?: string;
  tickLabelMargin?: number;
  tickStyle?: string;
  showGrid?: boolean;
  orientation?: string;
  visible?: boolean;
}

interface PlotPoint {
  x: number;
  include?: boolean;
}

interface NumberLinePlot {
  points: PlotPoint[];
  intervalNotation?: boolean;
  lineColor?: string;
  lineStyle?: string;
  shiftColor?: string;
  visible?: boolean;
  plotType?: string;
}

interface NumberLineData {
  type: string;
  name: string;
  id: string;
  renderMode?: string;
  majorTickSize?: number;
  minorTickSize?: number;
  margins: Margins;
  altText?: string;
  visible?: boolean;
  axisX: AxisConfig;
  axisY?: AxisConfig;
  plots: NumberLinePlot[];
  labels?: any[];
  tabAlign?: string;
}

// Configuration for number line rendering
interface NumberLineConfig {
  width: number;
  height: number;
  defaultMargins: Margins;
  arrowSize: number;
  fontSize: string;
  tickLabelOffset: number;
  intervalHeight: number;
  bracketSize: number;
}

const DEFAULT_NUMBERLINE_CONFIG: NumberLineConfig = {
  width: 380,
  height: 50,
  defaultMargins: { left: 10, top: 5, right: 10, bottom: 5 },
  arrowSize: 8,
  fontSize: '.75rem',
  tickLabelOffset: 15,
  intervalHeight: 20,
  bracketSize: 8,
};

/**
 * Converts a coordinate from the number line domain to SVG pixel coordinates
 */
function domainToPixel(
  value: number,
  axisMin: number,
  axisMax: number,
  pixelMin: number,
  pixelMax: number
): number {
  const domain = axisMax - axisMin;
  const range = pixelMax - pixelMin;
  return pixelMin + ((value - axisMin) / domain) * range;
}

/**
 * Generates tick marks for the number line
 */
function generateTicks(
  axisX: AxisConfig,
  pixelMin: number,
  pixelMax: number,
  centerY: number,
  config: NumberLineConfig
): string {
  let ticksContent = '';
  const tickSpacing = axisX.tickSpacing || 1;
  const majorTickSize = config.defaultMargins.top + 5;

  for (let value = axisX.minimum; value <= axisX.maximum; value += tickSpacing) {
    const x = domainToPixel(value, axisX.minimum, axisX.maximum, pixelMin, pixelMax);

    // Major tick mark
    ticksContent += `<line x1="${x}" y1="${centerY - majorTickSize / 2}" x2="${x}" y2="${
      centerY + majorTickSize / 2
    }" stroke="#333" stroke-width="1" />`;

    // Tick label
    if (axisX.labelSpacing && value % axisX.labelSpacing === 0) {
      const labelY = centerY + config.tickLabelOffset;
      ticksContent += `<text x="${x}" y="${labelY}" text-anchor="middle" font-family="${
        axisX.labelFontFamily || 'Inter'
      }" font-size="${axisX.labelFontSize || '0.68rem'}" fill="#333">${value}</text>`;
    }
  }

  return ticksContent;
}

/**
 * Generates arrow markers for the number line
 */
function generateArrows(
  axisX: AxisConfig,
  pixelMin: number,
  pixelMax: number,
  centerY: number,
  config: NumberLineConfig
): string {
  let arrowsContent = '';
  const arrowSize = config.arrowSize;

  if (axisX.showMinArrow) {
    // Left arrow
    arrowsContent += `<polygon points="${pixelMax},${centerY} ${pixelMax - arrowSize},${
      centerY - arrowSize / 2
    } ${pixelMax - arrowSize},${centerY + arrowSize / 2}" fill="#333" />`;
  }

  if (axisX.showMaxArrow) {
    // Right arrow
    arrowsContent += `<polygon points="${pixelMin - 5},${centerY} ${pixelMin + arrowSize},${
      centerY - arrowSize / 2
    } ${pixelMin + arrowSize},${centerY + arrowSize / 2}" fill="#333" />`;
  }

  return arrowsContent;
}

/**
 * Generates shaded intervals and brackets for plots
 */
function generatePlots(
  plots: NumberLinePlot[],
  axisX: AxisConfig,
  pixelMin: number,
  pixelMax: number,
  centerY: number,
  config: NumberLineConfig
): string {
  let plotsContent = '';

  plots.forEach((plot, plotIndex) => {
    if (!plot.visible) return;

    const lineColor = plot.lineColor || '#3250D2';
    const lineWidth = 3;
    const offsetY = centerY;

    if (plot.points.length >= 2) {
      const startPoint = plot.points[0];
      const endPoint = plot.points[1];

      // Handle infinite intervals (represented by values like -10, 10)
      const startX =
        startPoint.x <= axisX.minimum
          ? pixelMin - 20
          : domainToPixel(startPoint.x, axisX.minimum, axisX.maximum, pixelMin, pixelMax);
      const endX =
        endPoint.x >= axisX.maximum
          ? pixelMax + 20
          : domainToPixel(endPoint.x, axisX.minimum, axisX.maximum, pixelMin, pixelMax);

      // Shaded region
      plotsContent += `<line x1="${startX}" y1="${offsetY}" x2="${endX}" y2="${offsetY}" stroke="${lineColor}" stroke-width="${lineWidth}" opacity="0.7" />`;

      // Brackets/endpoints
      if (startPoint.x > axisX.minimum) {
        const bracketType = startPoint.include ? 'closed' : 'open';
        if (bracketType === 'closed') {
          plotsContent += `<circle cx="${startX}" cy="${offsetY}" r="3" fill="${lineColor}" />`;
        } else {
          plotsContent += `<circle cx="${startX}" cy="${offsetY}" r="3" fill="white" stroke="${lineColor}" stroke-width="2" />`;
        }
      }

      if (endPoint.x < axisX.maximum) {
        const bracketType = endPoint.include ? 'closed' : 'open';
        if (bracketType === 'closed') {
          plotsContent += `<circle cx="${endX}" cy="${offsetY}" r="3" fill="${lineColor}" />`;
        } else {
          plotsContent += `<circle cx="${endX}" cy="${offsetY}" r="3" fill="white" stroke="${lineColor}" stroke-width="2" />`;
        }
      }

      // Square brackets for interval notation
      if (plot.intervalNotation) {
        if (startPoint.x > axisX.minimum) {
          const bracketHeight = config.bracketSize;
          if (startPoint.include) {
            // Closed bracket [
            plotsContent += `<path d="M ${startX - 2} ${offsetY - bracketHeight / 2} L ${
              startX - 5
            } ${offsetY - bracketHeight / 2} L ${startX - 5} ${offsetY + bracketHeight / 2} L ${
              startX - 2
            } ${
              offsetY + bracketHeight / 2
            }" stroke="${lineColor}" stroke-width="2" fill="none" />`;
          } else {
            // Open bracket (
            plotsContent += `<path d="M ${startX - 2} ${offsetY - bracketHeight / 2} Q ${
              startX - 5
            } ${offsetY} ${startX - 2} ${
              offsetY + bracketHeight / 2
            }" stroke="${lineColor}" stroke-width="2" fill="none" />`;
          }
        }

        if (endPoint.x < axisX.maximum) {
          const bracketHeight = config.bracketSize;
          if (endPoint.include) {
            // Closed bracket ]
            plotsContent += `<path d="M ${endX + 2} ${offsetY - bracketHeight / 2} L ${endX + 5} ${
              offsetY - bracketHeight / 2
            } L ${endX + 5} ${offsetY + bracketHeight / 2} L ${endX + 2} ${
              offsetY + bracketHeight / 2
            }" stroke="${lineColor}" stroke-width="2" fill="none" />`;
          } else {
            // Open bracket )
            plotsContent += `<path d="M ${endX + 2} ${offsetY - bracketHeight / 2} Q ${
              endX + 5
            } ${offsetY} ${endX + 2} ${
              offsetY + bracketHeight / 2
            }" stroke="${lineColor}" stroke-width="2" fill="none" />`;
          }
        }
      }
    }
  });

  return plotsContent;
}

/**
 * Generates SVG for a number line component
 */
function generateNumberLineSVG(
  numberLineData: NumberLineData,
  config: NumberLineConfig = DEFAULT_NUMBERLINE_CONFIG
): string {
  const { axisX, margins, plots } = numberLineData;

  if (!axisX) {
    return '<div class="numberline-error">No axis configuration found</div>';
  }

  const totalMargins = {
    left: margins?.left || config.defaultMargins.left,
    top: margins?.top || config.defaultMargins.top,
    right: margins?.right || config.defaultMargins.right,
    bottom: margins?.bottom || config.defaultMargins.bottom,
  };

  const contentWidth = config.width - totalMargins.left - totalMargins.right;
  const contentHeight = config.height - totalMargins.top - totalMargins.bottom;

  const pixelMin = totalMargins.left;
  const pixelMax = totalMargins.left + contentWidth;
  const centerY = totalMargins.top + contentHeight / 2;

  // Calculate actual width based on arrows
  const arrowExtension = axisX.showMinArrow || axisX.showMaxArrow ? config.arrowSize + 5 : 0;
  const actualWidth = config.width + arrowExtension * 2;

  // Build SVG content
  let svgContent = `<svg width="${actualWidth}" height="${config.height}" xmlns="http://www.w3.org/2000/svg" class="numberline-svg">`;

  // Add styles
  svgContent += `<defs><style>.numberline-axis { stroke: #333; stroke-width: 2; }.numberline-tick { stroke: #333; stroke-width: 1; }.numberline-text { font-family: var(--inter); font-size: ${config.fontSize}rem; fill: #333; }.numberline-plot { stroke-width: 3; }.numberline-endpoint { stroke-width: 2; }</style></defs>`;

  // Main axis line
  svgContent += `<line x1="${pixelMin}" y1="${centerY}" x2="${pixelMax}" y2="${centerY}" class="numberline-axis" />`;

  // Add ticks and labels
  svgContent += generateTicks(axisX, pixelMin, pixelMax, centerY, config);

  // Add arrows
  svgContent += generateArrows(axisX, pixelMin, pixelMax, centerY, config);

  // Add plots (intervals, shading, brackets)
  if (plots && plots.length > 0) {
    svgContent += generatePlots(plots, axisX, pixelMin, pixelMax, centerY, config);
  }

  svgContent += '</svg>';

  return svgContent;
}

/**
 * Main preprocessing function to find and replace number line data with SVG
 */
export function preprocessNumberLineContent(content: string): string {
  const numberLineRegex = /%%NL%%(.*?)%%NL%%/gs;

  return content.replace(numberLineRegex, (match, jsonData) => {
    try {
      const numberLineData: NumberLineData = JSON.parse(jsonData.trim());

      // Validate the number line data
      if (!numberLineData.axisX) {
        console.error('Invalid number line data: missing axisX configuration');
        return '<div class="numberline-error">Invalid number line data: missing axis configuration</div>';
      }

      const svgHtml = generateNumberLineSVG(numberLineData);

      // Wrap in a div with number line class for styling
      return `<div className="numberline-container" data-id="${
        numberLineData.id || ''
      }">${svgHtml}</div>`;
    } catch (error) {
      console.error('Error parsing number line data:', error);
      return '<div class="numberline-error">Error rendering number line</div>';
    }
  });
}
